import commonStyle from "../commonStyles.module.css";
import pentraLogo from "../../../../assets/PentraLogoDarkFull.png";

import templateStyle from "./templateEditor.module.css";
import { useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import { PentestContext } from "../../../../contexts/PentestContext";

import Button from "../../../Common/Button/Button.tsx";
import DocumentViewer from "./components/DocumentViewer/DocumentViewer.tsx";
import { Download, AlertCircle, Loader2, RefreshCw, Plus, FileUp } from "lucide-react";
import {
  apiGetTemplateByID,
  apiTemplateUpdate,
  apiGetAppendixByTemplateID,
  apiTemplateDelete,
  apiTemplateCreate,
} from "../../../../sdk.ts";
import { AuthContext } from "../../../../contexts/AuthContext.ts";
import TemplateChooser from "./components/TemplateChooser/TemplateChooser.tsx";
import PdfTemplateWidget from "./components/PdfTemplateWidget/PdfTemplateWidget.tsx";
import DocxTemplateWidget from "./components/DocxTemplateWidget/DocxTemplateWidget.tsx";
import { TemplateContext } from "../../../../contexts/TemplateContext.ts";
import { AppendixContext } from "../../../../contexts/AppendixContext.ts";
import { AppendixFile, Template, defaultTemplate } from "../../../../sdkStructs.ts";
import { AppContext } from "../../../../contexts/AppContext.ts";
import { omit } from "lodash";

import { useDebounce } from "use-debounce";
import { areEqualHashes, calculateHash, calculateTemplateHash, TemplateHashes } from "./hashFunctions.ts";
import usePdfGeneration from "../../../../hooks/usePdfGeneration.ts";
import { downloadDocx, handleUploadDocxTemplate } from "./docxFunctions.ts";

export default function TemplateEditor() {
  const { token } = useContext(AuthContext);
  const [template, setTemplate] = useState<Template | null>(null);

  const { setShowModal, pushAlert } = useContext(AppContext);
  const [debouncedTemplate] = useDebounce(template, 1000);

  const [appendix, setAppendix] = useState<AppendixFile[]>([]);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [isDocxLoading, setIsDocxLoading] = useState(false);
  const { currentWindow, pentest, pentestVulns } = useContext(PentestContext);

  const [changed, setChanged] = useState(false);

  // Use PDF generation hook
  const { blob, loading, error: pdfError, generatePdf, resetPdf } = usePdfGeneration(token);

  // Create a combined loading state for template operations
  const isBusy = useMemo(
    () => isTemplateLoading || loading || isDocxLoading,
    [isTemplateLoading, loading, isDocxLoading],
  );

  // Use a ref to track if we're currently loading a template
  const isLoadingTemplate = useRef(false);
  // Track current template ID to avoid unnecessary regeneration
  const currentTemplateId = useRef<number | null>(null);
  // Track the last data used for PDF generation to prevent redundant generations
  const lastPdfGenerationData = useRef<TemplateHashes | undefined>();

  // API functions
  const loadTemplate = useCallback(
    async (id: number) => {
      if (isLoadingTemplate.current || id === currentTemplateId.current) return;

      isLoadingTemplate.current = true;
      setIsTemplateLoading(true);
      resetPdf();
      setChanged(false);
      currentTemplateId.current = id;

      try {
        const appendixResponse = await apiGetAppendixByTemplateID(token, id);
        setAppendix(appendixResponse.data);

        const response = await apiGetTemplateByID(token, id);
        // Preserve the PageOrder from the API response
        setTemplate(response.data);
      } catch (error) {
        console.error("Failed to load template:", error);
      } finally {
        isLoadingTemplate.current = false;
        setIsTemplateLoading(false);
      }
    },
    [token, setTemplate, resetPdf],
  );

  const createNewTemplate = useCallback(
    async (name: string) => {
      try {
        setIsTemplateLoading(true);
        const requestTemplate = { ...defaultTemplate, TemplateName: name };
        const templateWithoutId = omit(requestTemplate, "ID");
        const response = await apiTemplateCreate(token, templateWithoutId);
        const newTemplate = response.data;

        setTemplate(newTemplate);
        currentTemplateId.current = newTemplate.ID;
        pushAlert({
          title: "Success",
          text: "Template created successfully",
          type: "success",
          closeButton: true,
          time: 5000,
        });

      } catch (error) {
        console.error("Failed to create template:", error);
        pushAlert({
          title: "Error",
          text: "Failed to create template",
          type: "error",
          closeButton: true,
          time: 5000,
        });
      } finally {
        setIsTemplateLoading(false);
      }
    },
    [token, setTemplate, pushAlert],
  );

  const saveTemplateChanges = useCallback(async () => {
    if (!template) return;
    setChanged(false);
    try {
      const response = await apiTemplateUpdate(token, template.ID, template);
      console.log("Template saved successfully");
      return response;
    } catch (error) {
      console.error("Failed to save template:", error);
      setChanged(true); // Restore changed state if save fails
      throw error;
    }
  }, [token, template]);

  const deleteTemplate = useCallback(
    async (id: number) => {
      try {
        await apiTemplateDelete(token, id);
        setChanged(false);
        if (currentTemplateId.current === id) {
          setTemplate(null);
          currentTemplateId.current = null;
          resetPdf();
        }
      } catch (error) {
        console.error("Failed to delete template:", error);
        throw error;
      }
    },
    [token, setTemplate, resetPdf],
  );

  // Trigger PDF generation when template changes
  useEffect(() => {
    if (
      currentWindow === "template" &&
      pentest &&
      pentestVulns &&
      debouncedTemplate && // Ensure debounced template exists
      !isLoadingTemplate.current
    ) {
      // Calculate hash values for comparison
      const currentData: TemplateHashes = {
        templateId: debouncedTemplate.ID,
        pentestId: pentest.ID,
        vulnsHash: calculateHash(pentestVulns),
        appendixHash: calculateHash(appendix),
        templateHash: calculateTemplateHash(debouncedTemplate),
      };

      // Check if anything significant has changed
      if (lastPdfGenerationData.current && areEqualHashes(lastPdfGenerationData.current, currentData)) {
        console.log("Skipping PDF generation - data hasn't changed");
        return;
      }
      // Update the reference with current data
      lastPdfGenerationData.current = currentData;

      generatePdf(debouncedTemplate, pentest, pentestVulns, appendix);
    }
  }, [debouncedTemplate, pentest, pentestVulns, appendix, currentWindow, generatePdf]);

  // Mark template as changed on user edits
  useEffect(() => {
    if (template && !isLoadingTemplate.current) {
      setChanged(true);
    }
  }, [template]);

  // Download PDF blob
  const handleDownloadPdf = useCallback(() => {
    if (!blob || !template) {
      console.log("Unable to download PDF file");
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${template.TemplateName || "pentra-report"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [blob, template]);

  // Download DOCX version
  const handleDownloadDocx = useCallback(async () => {
    if (!template || !pentest || !pentestVulns) {
      console.log("Unable to download DOCX file");
      return;
    }

    try {
      setIsDocxLoading(true);

      // Clone the template and set FileType to docx
      const docxTemplate = { ...template, FileType: "docx" as "pdf" | "docx" };

      // Prepare the data for the PDF microservice
      const requestData = {
        template: docxTemplate,
        pentest: pentest,
        vulnerabilities: pentestVulns,
        appendix: appendix,
      };
      downloadDocx(token, requestData);
    } catch (error) {
      console.error("Failed to download DOCX:", error);
      pushAlert({
        title: "Error",
        text: "Failed to generate DOCX report",
        type: "error",
        closeButton: true,
        time: 5000,
      });
    } finally {
      setIsDocxLoading(false);
    }
  }, [token, template, pentest, pentestVulns, appendix, pushAlert]);

  const handleCreateTemplate = useCallback(() => {
    setShowModal("name", {
      title: "Create New Template",
      submit: createNewTemplate,
      close: () => setShowModal("none", undefined),
    });
  }, [createNewTemplate, setShowModal]);

  const contextAppendix = useMemo(() => {
    return { appendix, setAppendix };
  }, [appendix]);

  const previewIframe = useMemo(() => {
    if (blob) return <DocumentViewer blob={blob} />;
  }, [blob]);


  const forceRegenerate = useCallback(() => {
    generatePdf(template, pentest, pentestVulns, appendix);
  }, [generatePdf, template, pentest, pentestVulns, appendix]);

  const templateState = useMemo(() => {
    return { template, setTemplate, forceRegenerate };
  }, [template, forceRegenerate]);

  // Render DOCX download button component
  const renderDocxButton = () => {
    if (!template?.DocxPath) return null;

    return (
      <Button
        onClick={handleDownloadDocx}
        variant="primary"
        color="blue"
        textcolor="light"
        morepadding
        disabled={isDocxLoading}
      >
        {isDocxLoading ? (
          <Loader2
            className={`${templateStyle.iconMargin} ${templateStyle.spinningIcon}`}
            stroke="white"
            strokeWidth="2"
          />
        ) : (
          <Download stroke="white" strokeWidth="2" className={templateStyle.iconMargin} />
        )}
        <span>Download DOCX</span>
      </Button>
    );
  };

  if (currentWindow !== "template") return null;

  function getPdfArea() {
    if (template === null)
      // A message if the user has no pentests
      return (
        <div className={templateStyle.noPdfState}>
          <AlertCircle size={32} className={templateStyle.infoIcon} />
          <h3 className={templateStyle.noPdfTitle}>No Template Selected</h3>
          <p className={templateStyle.noPdfDescription}>Select or create a template to generate a preview.</p>
        </div>
      );
    if (pdfError)
      return (
        <>
          <div className={templateStyle.errorDiv}>
            <AlertCircle size={32} className={templateStyle.errorIcon} />
            <h3 className={templateStyle.errorText}>Error: {pdfError}</h3>
            {pdfError === "Failed to fetch" && <p>Please ensure the PDF microservice is running and accessible.</p>}
            <Button
              onClick={() => {
                lastPdfGenerationData.current = undefined;
                generatePdf(debouncedTemplate, pentest, pentestVulns, appendix);
              }}
              variant="secondary"
              color="blue"
              textcolor="light"
            >
              <RefreshCw stroke="var(--light-blue-icon)" strokeWidth="2" className={templateStyle.iconMargin} />
              <span>Retry</span>
            </Button>
          </div>

          {template?.DocxPath && <div className={templateStyle.downloadButtonsContainer}>{renderDocxButton()}</div>}
        </>
      );
    if (loading)
      return (
        <>
          <div className={templateStyle.loadingDiv}>
            <Loader2 className={`${templateStyle.loadingIcon} ${templateStyle.spinningIcon}`} />
            <h3 className={templateStyle.loadingText}>Generating PDF Preview...</h3>
          </div>

          {template?.DocxPath && <div className={templateStyle.downloadButtonsContainer}>{renderDocxButton()}</div>}
        </>
      );
    return (
      <>
        {previewIframe}
        <div className={templateStyle.downloadButtonsContainer}>
          {blob && (
            <Button onClick={handleDownloadPdf} variant="primary" color="blue" textcolor="light" morepadding>
              <Download stroke="white" strokeWidth="2" className={templateStyle.iconMargin} />
              <span>Download PDF</span>
            </Button>
          )}
          {renderDocxButton()}
        </div>
      </>
    );
  }

  function getSidebarContent() {
    if (template === null && !isTemplateLoading)
      // A message if the user has no pentests
      return (
        <div className={templateStyle.noTemplateState}>
          <h3 className={templateStyle.noTemplateTitle}>No Template Selected</h3>
          <p className={templateStyle.noTemplateDescription}>
            <span style={{ color: "white" }}>
              Create a new template or upload a DOCX file. You can check out the{" "}
              <a
                href="https://docs.pentra.ai/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--light-blue-icon)", textDecoration: "underline" }}
              >
                documentation
              </a>{" "}
              for more information.
            </span>
          </p>
          <div className={templateStyle.templateActionButtons}>
            <Button onClick={handleCreateTemplate} variant="primary" color="red" textcolor="light">
              <Plus className={templateStyle.iconMargin} stroke="white" strokeWidth="1.5" />
              Create PDF Template
            </Button>
            <Button
              onClick={() =>
                handleUploadDocxTemplate({
                  token,
                  setShowModal,
                  isTemplateLoading,
                  stateProps: { setLoadingFunc: setIsTemplateLoading, selectTemplateFunc: setTemplate, pushAlert },
                })
              }
              variant="primary"
              color="blue"
              textcolor="light"
            >
              <FileUp className={templateStyle.iconMargin} strokeWidth="1.5" />
              Upload DOCX Template
            </Button>
          </div>
        </div>
      );
    if (isTemplateLoading)
      // Loading template screen
      return (
        <div className={templateStyle.loadingTemplateState}>
          <Loader2 className={`${templateStyle.loadingIcon} ${templateStyle.spinningIcon}`} />
          <h3 className={templateStyle.loadingText}>Loading template...</h3>
        </div>
      );
    return (
      // Either docx or pdf template
      <AppendixContext.Provider value={contextAppendix}>
        {template?.DocxPath ? <DocxTemplateWidget onTemplateChange={() => { setChanged(true); }} /> : <PdfTemplateWidget />}
      </AppendixContext.Provider>
    );
  }

  return (
    <TemplateContext.Provider value={templateState}>
      <div className={templateStyle.wrapper}>
        <div className={commonStyle.contentRegion}>
          <div className={commonStyle.sidebarHeader}>
            <a href="/">
              <img className={commonStyle.logoImage + " " + commonStyle.uglyFix} src={pentraLogo} alt="Pentra Logo" />
            </a>
            <TemplateChooser
              onTemplateSelect={loadTemplate}
              onSave={saveTemplateChanges}
              onDelete={deleteTemplate}
              hasUnsavedChanges={changed}
              isTemplateLoading={isBusy}
            />
          </div>

          {getSidebarContent()}
        </div>

        <div className={templateStyle.pdfArea}>{getPdfArea()}</div>
      </div>
    </TemplateContext.Provider>
  );
}
